<template>
  <v-container>
    <PageHeader title="Manage Email Preferences">
      <template #subtitle>
        Control which email communications you receive from Community Boating
      </template>
    </PageHeader>

    <v-card class="mt-4">
      <v-card-title>Email Opt-Out Preferences</v-card-title>
      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          closable
          class="mb-4"
          @click:close="error = null"
        >
          {{ error }}
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="optOutGroups"
          :loading="isLoading"
          class="elevation-1"
        >
          <template #[`item.groupName`]="{ item }">
            <strong>{{ item.group?.groupName || "N/A" }}</strong>
          </template>

          <template #[`item.optedOut`]="{ item }">
            <v-chip :color="item.optedOut ? 'error' : 'success'" size="small">
              {{ item.optedOut ? "Opted Out" : "Subscribed" }}
            </v-chip>
          </template>

          <template #[`item.updatedOn`]="{ item }">
            {{ formatDate(item.updatedOn) }}
          </template>

          <template #[`item.actions`]="{ item }">
            <v-btn
              :color="item.optedOut ? 'success' : 'error'"
              :loading="updatingGroupId === item.group?.optOutGrpId"
              :disabled="isUpdating"
              size="small"
              @click="toggleOptOut(item)"
            >
              {{ item.optedOut ? "Subscribe" : "Opt Out" }}
            </v-btn>
          </template>

          <template #loading>
            <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
          </template>

          <template #no-data>
            <v-alert type="info" class="ma-4">
              No email groups available at this time.
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { useOptOutService } from "@/composables/useOptOutService";
import { useUtility } from "@/composables/useUtility";
import type { OptOutGroupStatus } from "../../../cbi-generator/generated/dto/OptOutGroupStatus";

const optOutService = useOptOutService();
const { formatDateNY } = useUtility();

const isLoading = ref(true);
const isUpdating = ref(false);
const updatingGroupId = ref<number | null>(null);
const error = ref<string | null>(null);
const optOutGroups = ref<OptOutGroupStatus[]>([]);

const headers = [
  {
    title: "Email Group",
    key: "groupName",
    sortable: true,
  },
  {
    title: "Status",
    key: "optedOut",
    sortable: true,
  },
  {
    title: "Date Updated",
    key: "updatedOn",
    sortable: true,
  },
  {
    title: "Actions",
    key: "actions",
    sortable: false,
    align: "end" as const,
  },
];

const formatDate = (date: Date | string | null | undefined): string => {
  return formatDateNY(date) || "N/A";
};

const loadOptOutStatus = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    const response = await optOutService.memberOptOutStatusGet();
    optOutGroups.value = response.groups || [];
  } catch (err: any) {
    console.error("Failed to load opt-out status:", err);
    error.value = "Failed to load your email preferences. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const toggleOptOut = async (item: OptOutGroupStatus) => {
  if (!item.group?.optOutGrpId) {
    error.value = "Invalid group information.";
    return;
  }

  try {
    isUpdating.value = true;
    updatingGroupId.value = item.group.optOutGrpId;
    error.value = null;

    // Toggle the opt-out status
    const newOptOutStatus = !item.optedOut;

    // Use the new memberOptOutStatusPost endpoint which handles token creation automatically
    const response = await optOutService.memberOptOutStatusPost(
      item.group.optOutGrpId,
      newOptOutStatus,
    );

    // Update the local state with the response (no need to reload)
    optOutGroups.value = response.groups || [];
  } catch (err: any) {
    console.error("Failed to update opt-out status:", err);
    error.value = "Failed to update your preference. Please try again.";
  } finally {
    isUpdating.value = false;
    updatingGroupId.value = null;
  }
};

onMounted(() => {
  loadOptOutStatus();
});
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
